import ClockIcon from '@ordr-as/icons/ClockIcon';
import type { KdsTaskDetail } from '@ordr-as/ordr-service-types/epos';
import { isOngoingStatus } from '../selectors';
import CardHeadTime from './CardHeadTime';
import TaskCardItems from './TaskCardItems';

type Props = {
  task: KdsTaskDetail;
  waitingForName?: string;
  nowMs: number;
  selected: boolean;
  focused: boolean;
  mutating: boolean;
  onOpen: () => void;
  onStart: () => void;
  onDone: () => void;
};

const EatInBadge = () => (
  <span className="inline-flex items-center text-extra-small font-semibold whitespace-nowrap px-2 py-0.5 rounded-full bg-grey-lightest text-grey-dark border border-brand-white">
    Eat in
  </span>
);

const OngoingTag = () => (
  <span className="inline-flex items-center text-extra-small font-semibold whitespace-nowrap px-2 py-0.5 rounded-full bg-yellow-light text-brand-secondary border border-brand-white">
    Ongoing
  </span>
);

const TaskCard = ({ task, waitingForName, nowMs, selected, focused, mutating, onOpen, onStart, onDone }: Props) => {
  const ongoing = isOngoingStatus(task.status);

  const cardClass = selected
    ? 'bg-purple-lightest ring-2 ring-brand-primary'
    : focused
      ? 'bg-brand-white ring-2 ring-brand-primary'
      : 'bg-brand-white';
  const headClass = ongoing && !selected ? 'bg-yellow-light' : '';

  return (
    <div
      data-kds-card
      data-card-id={task.id}
      className={`kds-card-in rounded flex flex-col overflow-hidden cursor-pointer ${cardClass}`}
      onClick={onOpen}
    >
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-grey-lightest ${headClass}`}>
        <span className="text-small font-semibold text-grey-dark">#{task.bookingOrderId}</span>
        {task.eatIn && <EatInBadge />}
        {ongoing && <OngoingTag />}
        <CardHeadTime createdUtc={task.createdUtc} nowMs={nowMs} />
      </div>

      <div className="px-4 py-3 flex flex-col gap-3 flex-1 min-h-[140px]">
        <TaskCardItems items={task.items ?? []} emphasized={ongoing} orderComment={task.orderComment} />
      </div>

      <div className="px-4 pb-4 pt-1 flex gap-2" onClick={(e) => e.stopPropagation()}>
        {task.status === 'QUEUED' && (
          <button
            className="h-7 flex-1 rounded font-semibold bg-brand-white text-brand-secondary border border-grey-medium hover:border-brand-primary disabled:opacity-60 transition-colors"
            disabled={mutating}
            onClick={onStart}
          >
            Start
          </button>
        )}
        {ongoing && (
          <button
            className="h-7 flex-1 rounded font-semibold bg-brand-secondary text-brand-white hover:bg-brand-primary disabled:opacity-60 transition-colors"
            disabled={mutating}
            onClick={onDone}
          >
            Done
          </button>
        )}
        {task.status === 'PENDING' && (
          <div className="h-7 w-full flex items-center gap-2 text-small italic text-grey-dark">
            <ClockIcon width={16} height={16} />
            {waitingForName ? `Waiting for ${waitingForName}…` : 'Waiting…'}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
